/* eslint-disable prefer-const */
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
  split,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { fromPromise } from '@apollo/client/link/utils';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { auth } from '../../firebaseConfig';
import api from './api';
import { isReadYourWrites, noteWriteLsn } from '../../src/utils/readYourWrites';

const isBrowser = typeof window !== 'undefined';

const authLink = setContext(async (_, { headers }) => {
  if (!auth.currentUser) return headers;
  const token = await auth.currentUser.getIdToken();
  if (!token) return headers;
  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  };
});

// Read-only: queries + subscriptions (RO replica)
const httpLinkRo = new HttpLink({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_API_RO_URL || process.env.NEXT_PUBLIC_GRAPHQL_API_URL,
});

// Write: mutations (primary)
const httpLinkMain = new HttpLink({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_API_URL,
});

let wsLink;

if (isBrowser) {
  wsLink = new WebSocketLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_WS_URL,
    options: {
      reconnect: true,
      lazy: true,
      connectionParams: async () => {
        if (!auth.currentUser) return {};
        const token = await auth.currentUser.getIdToken();
        if (!token) return {};
        return {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
    },
  });
}

const isSubscription = ({ query }: { query: any }) => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition'
    && definition.operation === 'subscription'
  );
};

const isQuery = ({ query }: { query: any }) => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition'
    && definition.operation === 'query'
  );
};

// 1.5.A: a mutation resolves only after the primary's LSN is noted, so the next REST read waits for it.
export const writeLsnLink = new ApolloLink((operation, forward) => {
  if (!isReadYourWrites()) return forward(operation);
  return forward(operation).flatMap((result) => fromPromise(api.get('db/lsn').then((res) => {
    if (res.success) noteWriteLsn(res.data?.lsn);
    return result;
  })));
});

// HTTP split: queries -> RO, mutations -> main
const httpSplit = split(
  isQuery,
  authLink.concat(httpLinkRo),
  from([authLink, writeLsnLink, httpLinkMain]),
);

// Top-level: subscriptions -> WS (RO), queries/mutations -> HTTP split
const link = isBrowser
  ? split(isSubscription, wsLink, httpSplit)
  : httpSplit;

const graphqlClient = new ApolloClient({
  link: from([link]),
  cache: new InMemoryCache(),
});

export default graphqlClient;
